import React, { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import './Multumim.css';

const CLINIC_DETAILS = {
    name: 'Evoo Medical',
    address: 'DN67C 286, România',
    mapsLink: 'https://www.google.com/maps/place/DN67C+286,+Romania/@45.9361974,23.562023,3a,75y,58.28h,86.08t/data=!3m7!1e1!3m5!1sqj13C2x_F568kjaXgNgWwA!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D3.924916850647719%26panoid%3Dqj13C2x_F568kjaXgNgWwA%26yaw%3D58.28049873252439!7i16384!8i8192!4m5!3m4!1s0x474c1f0d0f39dc13:0xbf26e1c04822e156!8m2!3d45.9353356!4d23.5617398?entry=tts&g_ep=EgoyMDI1MDgyNS4wIPu8ASoASAFQAw%3D%3D&skid=a70d152a-d07f-49e8-b88d-6ac971141bb6',
    mapsEmbedUrl: 'https://www.google.com/maps?q=DN67C%20286%2C%20Romania&output=embed',
    phone: '+40 752 875 121',
    phoneDial: '+40752875121',
    email: 'contact@evoomedical.ro',
    confirmationChannels: ['telefon', 'email'],
    workingHours: {
        weekdays: 'Luni - Vineri: 08:00 - 21:00',
        weekend: 'Sâmbătă - Duminică: închis'
    },
    accessInfo: 'Intrarea este prin ușile de sticlă din stânga, la recepție.',
    parkingInfo: 'Parcare disponibilă în fața clinicii, în limita locurilor.'
};

const DEFAULT_SUMMARY = {
    specialitate: 'Consultație medicală',
    data: '',
    ora: '',
    medic: '',
    email: '',
    telefon: ''
};

const formatDate = (value) => {
    if (!value) {
        return 'Data în curs de confirmare';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat('ro-RO', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(date);
};

const formatTime = (value) => value || 'Ora în curs de confirmare';

const toCalendarDate = (value) => {
    const iso = value.toISOString().replace(/[-:]/g, '').split('.')[0];
    return `${iso}Z`;
};

const getPreparationNote = (specialitate) => {
    const normalized = (specialitate || '').toLowerCase();
    const bloodKeywords = ['analize', 'sânge', 'laborator', 'biochimie', 'hematologie'];
    const needsFasting = bloodKeywords.some((keyword) => normalized.includes(keyword));

    if (needsFasting) {
        return 'Nu uitați să nu consumați alimente cu 12 ore înainte de recoltare.';
    }

    return 'Dacă programarea include analize de sânge, nu consumați alimente cu 12 ore înainte.';
};

const Multumim = () => {
    const location = useLocation();
    const trackingFiredRef = useRef(false);

    const summary = useMemo(() => {
        const stateSummary = location.state && location.state.summary ? location.state.summary : null;
        if (stateSummary) {
            sessionStorage.setItem('appointmentSummary', JSON.stringify(stateSummary));
            return { ...DEFAULT_SUMMARY, ...stateSummary };
        }

        try {
            const stored = sessionStorage.getItem('appointmentSummary');
            if (stored) {
                return { ...DEFAULT_SUMMARY, ...JSON.parse(stored) };
            }
        } catch (error) {
            return { ...DEFAULT_SUMMARY };
        }

        return { ...DEFAULT_SUMMARY };
    }, [location.state]);

    const formattedDate = formatDate(summary.data);
    const formattedTime = formatTime(summary.ora);
    const prepNote = getPreparationNote(summary.specialitate);

    const calendarData = useMemo(() => {
        if (!summary.data || !summary.ora) {
            return null;
        }

        const startDate = new Date(`${summary.data}T${summary.ora}:00`);
        if (Number.isNaN(startDate.getTime())) {
            return null;
        }

        const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
        const title = `Programare ${summary.specialitate}`;
        const details = `Solicitare programare pentru ${summary.specialitate}.${summary.medic ? ` Medic: ${summary.medic}.` : ''}`;

        return {
            title,
            details,
            start: toCalendarDate(startDate),
            end: toCalendarDate(endDate)
        };
    }, [summary.data, summary.ora, summary.specialitate, summary.medic]);

    const googleCalendarLink = useMemo(() => {
        if (!calendarData) {
            return null;
        }

        const query = new URLSearchParams({
            action: 'TEMPLATE',
            text: calendarData.title,
            dates: `${calendarData.start}/${calendarData.end}`,
            details: calendarData.details,
            location: CLINIC_DETAILS.address
        });

        return `https://calendar.google.com/calendar/render?${query.toString()}`;
    }, [calendarData]);

    const handleDownloadIcs = () => {
        if (!calendarData) {
            return;
        }

        const icsLines = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Evoo Medical//Programare//RO',
            'CALSCALE:GREGORIAN',
            'BEGIN:VEVENT',
            `UID:${Date.now()}@evoomedical.ro`,
            `DTSTAMP:${toCalendarDate(new Date())}`,
            `DTSTART:${calendarData.start}`,
            `DTEND:${calendarData.end}`,
            `SUMMARY:${calendarData.title}`,
            `DESCRIPTION:${calendarData.details}`,
            `LOCATION:${CLINIC_DETAILS.address}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ];

        const blob = new Blob([`${icsLines.join('\r\n')}\r\n`], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'programare-evoo-medical.ics';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (!trackingFiredRef.current) {
            trackingFiredRef.current = true;

            try {
                if (typeof window.gtag === 'function') {
                    window.gtag('event', 'lead', {
                        page_path: window.location.pathname,
                        page_title: document.title
                    });

                    const conversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
                    const conversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
                    if (conversionId && conversionLabel) {
                        window.gtag('event', 'conversion', {
                            send_to: `${conversionId}/${conversionLabel}`
                        });
                    }
                }

                if (typeof window.fbq === 'function') {
                    window.fbq('track', 'Lead');
                }
            } catch (error) {
                // Ignore tracking failures
            }
        }
    }, []);

    // Email notification disabled - already handled by backend during appointment creation
    // useEffect(() => {
    //     if (!summary.email || emailSentRef.current) {
    //         return;
    //     }

    //     const alreadySent = sessionStorage.getItem('thankYouEmailSent');
    //     if (alreadySent) {
    //         return;
    //     }

    //     emailSentRef.current = true;

    //     const subject = 'Am primit solicitarea ta - Evoo Medical';
    //     const messageText = `Salut ${summary.prenume || ''} ${summary.nume || ''},\n\nAm primit solicitarea ta pentru ${summary.specialitate}. Revenim in maximum 24 de ore pentru confirmare.\n\nMultumim,\nEchipa ${CLINIC_DETAILS.name}`.trim();
    //     const messageHtml = messageText.replace(/\n/g, '<br />');

    //     apiService
    //         .sendEmail({
    //             to: summary.email,
    //             subject,
    //             text: messageText,
    //             html: messageHtml
    //         })
    //         .then(() => {
    //             sessionStorage.setItem('thankYouEmailSent', 'true');
    //         })
    //         .catch(() => {
    //             // Ignore email errors to avoid blocking UX
    //         });
    // }, [summary.email, summary.nume, summary.prenume, summary.specialitate]);

    return (
        <div className="thankyou-page">
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <section className="thankyou-hero">
                <div className="thankyou-card">
                    <div className="thankyou-icon" aria-hidden="true">
                        <svg viewBox="0 0 120 120" role="img" aria-label="Succes">
                            <circle cx="60" cy="60" r="56" fill="#E8F6EF" stroke="#1F9A5A" strokeWidth="4" />
                            <path d="M35 62l16 16 34-36" fill="none" stroke="#1F9A5A" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h1>Solicitare Trimisa cu Succes</h1>
                    <p className="thankyou-subtitle">
                        Programare pentru: <strong>{summary.specialitate}</strong> / Data: <strong>{formattedDate}</strong>{' '}
                        {summary.ora ? `/ Ora: ${formattedTime}` : ''}
                    </p>
                    {summary.medic && (
                        <p className="thankyou-meta">Medic: {summary.medic}</p>
                    )}
                </div>
            </section>

            <section className="thankyou-grid">
                <article className="thankyou-panel">
                    <h2>Ce urmează</h2>
                    <ul>
                        <li>Vă vom contacta în maximum 24 de ore pentru confirmare.</li>
                        <li>Metoda de contact: {CLINIC_DETAILS.confirmationChannels.join(', ')}.</li>
                        <li>{CLINIC_DETAILS.workingHours.weekdays}</li>
                        <li>{CLINIC_DETAILS.workingHours.weekend}</li>
                        <li>Dacă trimiteți solicitarea sâmbătă seara, cele 24 de ore încep luni la 08:00.</li>
                    </ul>
                </article>

                <article className="thankyou-panel">
                    <h2>Ghidul pacientului</h2>
                    <div className="thankyou-info">
                        <div>
                            <h3>Acces & parcare</h3>
                            <p>{CLINIC_DETAILS.parkingInfo}</p>
                            <p>{CLINIC_DETAILS.accessInfo}</p>
                        </div>
                        <div>
                            <h3>Pregătire</h3>
                            <p>{prepNote}</p>
                        </div>
                    </div>
                </article>
            </section>

            <section className="thankyou-map">
                <div className="thankyou-panel">
                    <div className="map-header">
                        <div>
                            <h2>Ne găsiți aici</h2>
                            <p>{CLINIC_DETAILS.address}</p>
                        </div>
                        <a className="map-link" href={CLINIC_DETAILS.mapsLink} target="_blank" rel="noreferrer">
                            Deschide în Google Maps
                        </a>
                    </div>
                    <div className="map-embed">
                        <iframe
                            title="Harta Evoo Medical"
                            src={CLINIC_DETAILS.mapsEmbedUrl}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </section>

            <section className="thankyou-actions">
                <div className="thankyou-panel">
                    <h2>Rezervă în agenda ta</h2>
                    <div className="action-buttons">
                        <button type="button" className="primary" onClick={handleDownloadIcs} disabled={!calendarData}>
                            Adaugă în calendar
                        </button>
                        {googleCalendarLink && (
                            <a className="secondary" href={googleCalendarLink} target="_blank" rel="noreferrer">
                                Deschide în Google Calendar
                            </a>
                        )}
                        <a className="ghost" href={`tel:${CLINIC_DETAILS.phoneDial}`}>
                            Urgență: sună acum
                        </a>
                    </div>
                    <p className="contact-note">
                        Telefon: <a href={`tel:${CLINIC_DETAILS.phoneDial}`}>{CLINIC_DETAILS.phone}</a> · Email:{' '}
                        <a href={`mailto:${CLINIC_DETAILS.email}`}>{CLINIC_DETAILS.email}</a>
                    </p>
                </div>
            </section>

            <section className="thankyou-proof">
                <div className="thankyou-panel">
                    <h2>Încredere din partea pacienților</h2>
                    <div className="proof-grid">
                        <div className="proof-card">
                            <div className="proof-rating">4.9/5</div>
                            <p>Evaluări Google de la pacienți reali</p>
                        </div>
                        <div className="proof-card">
                            <p>"Programare rapidă și personal empatic. Recomand cu încredere."</p>
                            <span>— Ana M.</span>
                        </div>
                        <div className="proof-card">
                            <p>"Mi-au confirmat telefonic în aceeași zi. Totul clar și organizat."</p>
                            <span>— Mihai I.</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Multumim;
